/* eslint-disable react-refresh/only-export-components */
import { DimeSeparator, DimeToggle } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { Bold, Italic, Underline } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/toggle')({
  component: ToggleDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

const SIZES: { value: 'default' | 'sm' | 'lg'; label: string; description: string }[] = [
  { value: 'sm', label: 'sm', description: 'Small — h-7.' },
  { value: 'default', label: 'default', description: 'Default — h-8.' },
  { value: 'lg', label: 'lg', description: 'Large — h-9.' },
];

function ToggleDocs() {
  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 text-3xl font-bold">Toggle</h1>
      <p className="mb-8 text-muted-foreground">
        A two-state button that can be on or off. Built on Radix UI. Use{' '}
        <code className="font-mono text-sm">pressed</code> /{' '}
        <code className="font-mono text-sm">defaultPressed</code> and{' '}
        <code className="font-mono text-sm">onPressedChange</code> to control state.
      </p>

      <DocsSection title="Default">
        <Preview>
          <DimeToggle aria-label="Toggle bold">
            <Bold />
          </DimeToggle>
          <DimeToggle aria-label="Toggle italic">
            <Italic />
          </DimeToggle>
          <DimeToggle aria-label="Toggle underline">
            <Underline />
          </DimeToggle>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Icon-only toggles. Always provide an <code className="font-mono">aria-label</code> when
          there is no visible text.
        </p>
      </DocsSection>

      <DocsSection title="Outline">
        <Preview>
          <DimeToggle variant="outline" aria-label="Toggle bold outline">
            <Bold />
          </DimeToggle>
          <DimeToggle variant="outline" aria-label="Toggle italic outline">
            <Italic />
          </DimeToggle>
          <DimeToggle variant="outline" aria-label="Toggle underline outline">
            <Underline />
          </DimeToggle>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">variant="outline"</code> for a bordered style.
        </p>
      </DocsSection>

      <DocsSection title="With Text">
        <Preview>
          <DimeToggle variant="outline" aria-label="Toggle bold with text">
            <Bold />
            Bold
          </DimeToggle>
          <DimeToggle variant="outline" aria-label="Toggle italic with text">
            <Italic />
            Italic
          </DimeToggle>
          <DimeToggle variant="outline" aria-label="Toggle underline with text">
            <Underline />
            Underline
          </DimeToggle>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Place an icon before the label. The icon inherits active and muted colour states
          automatically.
        </p>
      </DocsSection>

      <DocsSection title="Sizes">
        <Preview>
          {SIZES.map((size) => (
            <DimeToggle
              key={size.value}
              variant="outline"
              size={size.value}
              aria-label={`Bold ${size.label}`}
            >
              <Bold />
              Bold
            </DimeToggle>
          ))}
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Size</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {SIZES.map((size) => (
              <tr key={size.value} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{size.value}</td>
                <td className="py-2 text-muted-foreground">{size.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DimeToggle variant="outline" aria-label="Disabled unpressed" disabled>
            <Bold />
            Bold
          </DimeToggle>
          <DimeToggle variant="outline" aria-label="Disabled outline" disabled>
            <Italic />
            Italic
          </DimeToggle>
          <DimeToggle variant="outline" aria-label="Disabled pressed" pressed disabled>
            <Underline />
            Pressed
          </DimeToggle>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">disabled</code> to make the toggle non-interactive.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              { prop: 'variant', type: '"default" | "outline"', desc: 'Visual style.' },
              { prop: 'size', type: '"default" | "sm" | "lg"', desc: 'Toggle size.' },
              { prop: 'pressed', type: 'boolean', desc: 'Controlled pressed state.' },
              {
                prop: 'defaultPressed',
                type: 'boolean',
                desc: 'Initial pressed state (uncontrolled).',
              },
              {
                prop: 'onPressedChange',
                type: '(pressed: boolean) => void',
                desc: 'Fires when state changes.',
              },
              { prop: 'disabled', type: 'boolean', desc: 'Prevents interaction.' },
              { prop: 'asChild', type: 'boolean', desc: 'Merges props onto the child element.' },
            ].map((row) => (
              <tr key={row.prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.prop}</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{row.type}</td>
                <td className="py-2 text-muted-foreground">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
